import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Drawer,
  Dropdown,
  Form,
  Input,
  Menu,
  message,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import { ReactComponent as FilterIcon } from '../../Images/FilterIcon.svg';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import {
  createDieType,
  deleteDieType,
  editDieType,
  fetchDieTypeList,
} from '../../slices/die-type';

function DieTypeList() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [editDieGroup, setEditDieGroup] = useState(null); // Track the category being edited
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(10);
  const [form] = Form.useForm();
  const [orderBy, setOrderBy] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const dispatch = useDispatch();
  const { user: currentUser } = useSelector((state) => state.auth);
  const {data, totalRecord, status} = useSelector(state => state.dieType);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const rowSelection = {
    selectedRowKeys,
    onChange: selectedKeys => {
      setSelectedRowKeys(selectedKeys);
    },
  };

  useEffect(() => {
    dispatch(fetchDieTypeList({ page, pagesize, ...(search && { search: search }), ...(orderBy && { ordering: orderBy }) }));
  }, [page, pagesize, search, orderBy]);
  const handleSearch = e => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page on new search
  
  };

  const GetSortingEnumValue = (sortField, sortOrder) => {
    let sortingEnumKey = "id";

    if (sortField === "name" && sortOrder === "ascend") {
        sortingEnumKey = "name";
    }
    if (sortField === "name" && sortOrder === "descend") {
        sortingEnumKey = "-name";
    }

    return sortingEnumKey;
  };

  const resetFilter = () => {
        setSearch('');
        setPage(1);
        setOrderBy('');
  };
  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleTableChange = async (pagination, filters, sorter, ordering) => {
    if (sorter && sorter.columnKey && sorter.order) {
       const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
       setOrderBy(orderBy);
    }
    setPageSize(pagination.pageSize);
  };
  const showDrawer = () => {
    setOpen(true);
    setEditDieGroup(null); // Reset edit state when opening drawer for adding a new category
  };
  const onClose = () => {
    setOpen(false);
    form.resetFields(); // Reset form fields when closing
  };
  const onSubmit = async values => {
    try {
      if (editDieGroup) {
        await dispatch(editDieType({categoryId: editDieGroup.id, categoryData: values})).unwrap();
        message.success('Die Type updated successfully!');
      } else {
        await dispatch(createDieType(values)).unwrap();
        message.success('Die Type added successfully!');
      }
      form.resetFields();
      setOpen(false); // Close the drawer
      dispatch(fetchDieTypeList({page, pagesize, search})); // Refresh category list after update
    } catch (error) {
        if (typeof error === 'object' && error !== null) {
            const messages = [];
            for (const key in error) {
                if (Array.isArray(error[key])) {
                    messages.push(`${key}: ${error[key].join(', ')}`);
                }
            }

            if (messages.length > 0) {
                message.error(messages.join('; '));
            } else {
                message.error('An unknown error occurred.');
            }
        } else if (typeof error === 'string') {
            message.error(error); // Handle string error
        } else if (error instanceof Error) {
            message.error(error.message); // Handle Error object
        } else {
            message.error('An unknown error occurred.');
        }
    }
  };
  const handleEdit = dieGroup => {
    setEditDieGroup(dieGroup); // Set selected die group for editing
    form.setFieldsValue({
      name: dieGroup.name,
      description: dieGroup.description,
    }); // Pre-fill form fields
    setOpen(true); // Open the drawer for editing
  };

  const handleDelete = id => {
    if (!id) {
      message.error('Missing ID for deletion!');
      return;
    }

    dispatch(deleteDieType(id))
      .unwrap()
      .then(() => {
        message.success('Die Type Archive Successfully!');
      })
      .catch(error => {
        if (typeof error === 'object' && error !== null) {
          const messages = [];
          for (const key in error) {
              if (Array.isArray(error[key])) {
                  messages.push(`${key}: ${error[key].join(', ')}`);
              }
          }

          if (messages.length > 0) {
              message.error(messages.join('; '));
          } else {
              message.error('An unknown error occurred.');
          }
      } else if (typeof error === 'string') {
          message.error(error); // Handle string error
      } else if (error instanceof Error) {
          message.error(error.message); // Handle Error object
      } else {
          message.error('An unknown error occurred.');
      }
      });
  };
  const dataSource = data.map((item, index) => ({
    ...item,
    key: item.id || index,
    SrNo: (page - 1) * pagesize + index + 1, // Calculate Sr. No. based on page and limit
  }));

  const allColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo', // Ensure 'id' is the key here, as it's your unique identifier
      sorter: false,
    },
    {
      title: 'Die Type  Name',
      dataIndex: 'name', // Map to the 'name' property
      key: 'name',
      sorter: true,
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'action',
      render: (text, record) => (
        <Space>
            {currentUser.permissions.includes("die|change_dietype") && (
                <Link onClick={() => handleEdit(record)}>
                  <Tooltip title={'Edit'}>
                    <EditIcon />
                  </Tooltip>
                </Link>
            )}
            {currentUser.permissions.includes("die|delete_dietype") && (
                <Link>
                  <Tooltip placement="top" title={'Archive'}>
                    <Popconfirm
                      title="Are you sure you want to archive this record?"
                      onConfirm={() => handleDelete(record.id)} // Trigger delete on confirmation
                      onCancel={() => message.info('Action cancelled')} // Optional cancel action
                      okText="Yes"
                      cancelText="No"
                    >
                      <Archive style={{cursor: 'pointer'}} />
                    </Popconfirm>
                  </Tooltip>
                </Link>
            )}
        </Space>
      ),
    },
  ];

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'name',
    'action',
  ]);

  const handleColumnVisibilityChange = checkedValues => {
    setCheckedList(checkedValues);
  };

  const filteredColumns = allColumns.filter(column =>
    checkedList.includes(column.key),
  );
  const columnMenu = (
    <Menu className="packagelistdropdown">
      <Menu.Item>
        <Checkbox.Group
          value={checkedList}
          onChange={handleColumnVisibilityChange}
        >
          <Row>
            <Col span={12}>
              <Checkbox value="name">Die Type Name</Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="SrNo" disabled>
                Sr. No.
              </Checkbox>
            </Col>
            <Col span={12}>
              <Checkbox value="action" disabled>
                Action
              </Checkbox>
            </Col>
          </Row>
        </Checkbox.Group>
      </Menu.Item>
    </Menu>
  );

  return (
    <>
      <Row className="pagenamerow" justify="space-between" align="middle">
        <Col>
          <h2 style={{margin: 0}}>Die Types</h2>
          <div className="bredcrumbwrp">
            <Link to="/die-profilelist" className="back">
              <Back /> BACK
            </Link>
            <Breadcrumb
                items={[
                    { title: <Link to="/dashboard">Home</Link> },
                    { title: 'Die Types' }
                ]}
            />
          </div>
        </Col>
        <Col style={{display: 'flex', alignItems: 'center'}}>
          <Space>
            <Input
              placeholder="Search"
                  className='searchbox'
              prefix={<SearchOutlined />}
              value={search}
              style={{width: 200}}
              onChange={handleSearch}
            />
            <Tooltip placement="top" title="Reset Filter">
            <Button type="primary" disabled={!search && !orderBy} className='iconlink' onClick={resetFilter}>
                <FilterIcon />
              </Button>
            </Tooltip>
              {currentUser.permissions.includes("die|add_dietype") && (
                  <Button icon={<PlusOutlined />} type="primary" onClick={showDrawer}>
                    Add Die Type
                  </Button>
              )}
            <Tooltip placement="top" title="Archive">
              <Link to="/archive-die-type" className="iconlink">
                {' '}
                <Archiveicon style={{cursor: 'pointer'}} />
              </Link>
            </Tooltip>
            <Link>
              <Tooltip placement="topLeft" title="Column Filter Options">
                <Dropdown
                  overlay={columnMenu}
                  trigger={['click']}
                  placement="bottomRight"
                  visible={dropdownVisible}
                  onVisibleChange={setDropdownVisible}
                >
                  <Button
                    type="primary"
                    icon={<ColumnList />}
                    onClick={e => e.preventDefault()}
                  />
                </Dropdown>
              </Tooltip>
            </Link>
          </Space>
        </Col>
      </Row>

      <Row>
        <Col span={24}>
          <Table
            rowKey="id"
            columns={filteredColumns}
            rowSelection={rowSelection}
            dataSource={dataSource}
            loading={status === 'loading'}
            pagination={{
                defaultCurrent:page,
                defaultPageSize: pagesize,
                total: totalRecord,
                showTotal: (total) => `Total ${total} items`,
                showSizeChanger: true,
                onChange: handlePageChange,
                locale: {
                    items_per_page: 'Items / Page', // Text for the size changer
                },
            }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>

      <Drawer
        title={editDieGroup ? 'Edit Die Type' : 'Add New Die Type'}
        onClose={onClose}
        open={open}
      >
        <Form layout="vertical" form={form} onFinish={onSubmit}>
          <Form.Item
            name="name"
            label="Die Type Name"
            rules={[{required: true, message: 'Enter a valid Die Type name!'}, {
              pattern: /^[a-zA-Z\s]+$/,
              message: 'Die Type can only include letters and spaces!',
            }]}
          >
            <Input placeholder="Enter Die Type Name" onChange={(e) => {
                const value = e.target.value;
                const formattedValue = value.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
                form.setFieldsValue({ name: formattedValue }); // Update Form state
            }} />
          </Form.Item>
          <div className="flex justify-end">
            <Button type="primary" htmlType="submit" icon={<PlusOutlined />}>
              {editDieGroup ? 'Update Die Type' : 'Add Die Type'}
            </Button>
          </div>
        </Form>
      </Drawer>
    </>
  );
}

export default DieTypeList;
